import { MatrixClient } from 'matrix-js-sdk/src';
import moment from 'moment';
import { useIntervalWhen } from 'rooks';

import { getInvitationDetails } from 'api/meedioBackend/getInvitationDetails';
import { getMeetingByRoomId } from 'api/meedioBackend/getMeetingByRoomId';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents, isValidRoomScheduledStateEvent } from 'utils/matrixUtils';
import { getCurrentOrNextOccurrenceWindow, JOIN_PREROOM_THRESHOLD_MS } from 'utils/occurrenceWindow';

const DEFAULT_INTERVAL = 10000;

interface UseScheduledStartPollingProps {
  roomId?: string;
  currentScheduledStart?: string;
  /**
   * BUG-074: the meeting's currently-known end time. Passing it lets the poll
   * detect end-only edits (the organiser extended the meeting without moving the
   * start) and propagate the new window via `onScheduledStartChange`'s second arg.
   */
  currentScheduledEnd?: string;
  onScheduledStartChange: (scheduledStart: string, scheduledEnd?: string) => void;
  /**
   * BUG-007 Fix A: called when polling detects the recurring meeting has already
   * started early (backend `started_at` falls inside the current occurrence window).
   * `RoomIdentityProvider` uses this to flip `isStarted: true` so pre-joined
   * participants are admitted without waiting for wall-clock `scheduled_at`.
   */
  onMeetingStarted?: () => void;
  matrixClient: MatrixClient;
  inviteToken?: string;
  /**
   * Called when a poll response reports `cancelled_at` on the meeting.
   * `RoomIdentityProvider` uses this to navigate the waiting user to
   * `/404` within one poll cycle (≤10s). See spec §T-006 and
   * research/business-flows.md §F-19.
   */
  onMeetingCancelled?: () => void;
}

const useScheduledStartPolling = ({
  roomId,
  currentScheduledStart,
  currentScheduledEnd,
  onScheduledStartChange,
  onMeetingStarted,
  matrixClient,
  inviteToken,
  onMeetingCancelled,
}: UseScheduledStartPollingProps) => {
  // Poll for every scheduled meeting — including messenger-linked rooms.
  // Cancel detection (F-19) and schedule-update banners (T-013/Screen 8)
  // require this path for both entry surfaces; the original BUG-021 guard
  // was removed in T-009 because `getMeetingByRoomId` is a backend call and
  // does not contribute to Matrix homeserver load.
  const shouldPoll = !!roomId && !!currentScheduledStart && moment(currentScheduledStart).isSameOrAfter(moment());

  const handlePolling = () => {
    if (!roomId) return;

    const room = matrixClient.getRoom(roomId);
    const content = room?.currentState
      ?.getStateEvents(CustomMeedioMatrixEvents.IS_SCHEDULED_CALL_STATE_EVENT, '')
      ?.getContent();

    if (content && isValidRoomScheduledStateEvent(content)) {
      const rawStartTime = content.isStarted ? content.startsAt : undefined;
      const startTime =
        typeof rawStartTime === 'string'
          ? rawStartTime
          : rawStartTime instanceof Date
          ? rawStartTime.toISOString()
          : undefined;
      if (startTime && startTime !== currentScheduledStart) {
        onScheduledStartChange(startTime);
        return;
      }
    }

    getMeetingByRoomId(roomId)
      .then((meetingResponse) => {
        if (!meetingResponse) return;
        // Cancellation detection (F-19): check cancelled_at BEFORE the
        // started_at transition. Invoking `onMeetingCancelled` lets
        // `RoomIdentityProvider` redirect to /404 within one poll cycle.
        if (meetingResponse.cancelled_at) {
          onMeetingCancelled?.();
          return;
        }
        // Recurring meetings: the backend `scheduled_at` is the *original*
        // anchor (set at creation, never moves), so comparing against
        // `currentScheduledStart` would fight any local advance after an
        // explicit end. Use the current/next occurrence window instead, and
        // refuse to roll back when the local clock has already advanced past
        // the backend's tracked occurrence (e.g. `RoomIdentityProvider`
        // advanced to tomorrow before the backend cron updates
        // `current_occurrence_*`). Forward jumps are still accepted so the
        // pre-meeting countdown stays in sync once the backend rolls forward.
        if (meetingResponse.is_recurring) {
          const window = getCurrentOrNextOccurrenceWindow(meetingResponse);
          const occurrenceStart = window ? new Date(window.startMs).toISOString() : null;
          const occurrenceEnd = window ? new Date(window.endMs).toISOString() : undefined;
          if (!occurrenceStart) return;
          // BUG-007 Fix A: if the backend reports the meeting has already started and
          // `started_at` falls inside the current occurrence window, signal the
          // identity context to flip `isStarted: true`. This admits pre-joined
          // participants within the next poll cycle (~10 s) when the organiser starts
          // the recurring meeting early — without waiting for wall-clock startMs.
          if (onMeetingStarted && meetingResponse.started_at && window) {
            const startedAtMs = Date.parse(meetingResponse.started_at);
            if (startedAtMs >= window.startMs - JOIN_PREROOM_THRESHOLD_MS && startedAtMs < window.endMs) {
              onMeetingStarted();
            }
          }
          const startUnchanged = occurrenceStart === currentScheduledStart;
          // BUG-074: an end-only edit keeps the occurrence start but changes its
          // end — propagate it even when the start is unchanged or a rollback.
          // Only when we already have a baseline end to compare against, so the
          // first poll (no known end yet) does not fire spuriously.
          const endChanged = !!occurrenceEnd && !!currentScheduledEnd && occurrenceEnd !== currentScheduledEnd;
          if (startUnchanged && !endChanged) return;
          const startRollback =
            !!currentScheduledStart && Date.parse(occurrenceStart) <= Date.parse(currentScheduledStart);
          // Don't roll the start backward; only forward jumps move the start. If
          // the start would roll back but the end changed, keep the known start.
          if (startRollback && !startUnchanged) {
            if (endChanged) onScheduledStartChange(currentScheduledStart, occurrenceEnd);
            return;
          }
          onScheduledStartChange(occurrenceStart, occurrenceEnd);
          return;
        }
        // Instant rooms have null scheduled_at; nothing to poll against.
        if (
          onMeetingStarted &&
          meetingResponse.started_at &&
          meetingResponse.scheduled_at &&
          !meetingResponse.is_recurring
        ) {
          const startedAtMs = Date.parse(meetingResponse.started_at);
          const scheduledStartMs = Date.parse(meetingResponse.scheduled_at);
          const endsAtMs = meetingResponse.ends_at ? Date.parse(meetingResponse.ends_at) : Number.POSITIVE_INFINITY;
          if (startedAtMs >= scheduledStartMs - JOIN_PREROOM_THRESHOLD_MS && startedAtMs < endsAtMs) {
            onMeetingStarted();
          }
        }
        const scheduledAt = meetingResponse.started_at ?? meetingResponse.scheduled_at;
        const scheduledEnd = meetingResponse.ends_at ?? undefined;
        const startChanged = !!scheduledAt && scheduledAt !== currentScheduledStart;
        // Only treat the end as changed once a baseline end is known, so the
        // first poll does not fire on a meeting that merely has an end time.
        const endChanged = !!scheduledEnd && !!currentScheduledEnd && scheduledEnd !== currentScheduledEnd;
        if (scheduledAt && (startChanged || endChanged)) onScheduledStartChange(scheduledAt, scheduledEnd);
      })
      .catch((e) => {
        logger.error('Error polling scheduled start', e);
        if (!inviteToken) return;

        const userId = matrixClient.getUserId();
        if (!userId) return;

        getInvitationDetails(inviteToken, userId)
          .then((response) => {
            const startedAt = response.room?.started_at;
            if (startedAt && startedAt !== currentScheduledStart) {
              onScheduledStartChange(startedAt);
            }
          })
          .catch((fallbackErr) => logger.error('Error polling via invitation token', fallbackErr));
      });
  };

  useIntervalWhen(handlePolling, DEFAULT_INTERVAL, shouldPoll, false);
};

export default useScheduledStartPolling;
